import React from 'react'
import { useState, useEffect, useRef } from 'react'

import {
  ActionIcon,
  Modal,
  Select,
  Group,
  Text,
  TextInput,
  CloseButton,
  Divider,
  Skeleton,
  Tooltip,
} from '@mantine/core'
import {
  Dropzone,
  IMAGE_MIME_TYPE,
  PDF_MIME_TYPE,
  MS_WORD_MIME_TYPE,
  MS_EXCEL_MIME_TYPE,
} from '@mantine/dropzone'
import { useForm } from '@mantine/form'
import { File } from 'lucide-react'
import { Step, VerificationOfEmployment } from 'types/graphql'

import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DateInput from 'src/components/Inputs/DateInput/DateInput'
import DateShortcutButton from 'src/components/Inputs/DateShortcutButton/DateShortcutButton'
import ReadOnlyField from 'src/components/Overlays/CompletionMethodModal/ReadOnlyField'
import VoECompletionFields, {
  EMPTY_VOE_COMPLETION,
  VoECompletionValue,
  defaultVoECompletionPlatform,
  isVoECompletionValid,
  toVoECompletionInput,
} from 'src/components/Overlays/CompletionMethodModal/VoECompletionFields'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { useCurrentCompanyContext } from 'src/context/CurrentCompanyContext'
import {
  CREATE_DOCUMENT,
  GET_DOCUMENT_INTELLIGENCE,
  REVIEW_DOCUMENT_PREDICTIONS,
  UPDATE_DOCUMENT,
} from 'src/graphql/documents.graphql'
import {
  DocumentCategory,
  DocumentUserStatus,
  UploadStatus,
  FileContentType,
} from 'src/graphql/types/documents'
import { stepTypeToDocumentCategoryMapping } from 'src/graphql/types/steps'
import { VoEStatus } from 'src/graphql/types/verificationOfEmployments'
import IconSparkle from 'src/icons/IconSparkle'
import IconUpload from 'src/icons/IconUpload'
import { useBulkUploadDocumentProgress } from 'src/lib/bulkUploadDocumentProgress'
import { buildDocumentCategoryOptions } from 'src/lib/documentCategory.utils'
import { AI_HIGH_CONFIDENCE_THRESHOLD } from 'src/lib/documentIntelligence'
import { TRACKED_EXPIRATION_CATEGORIES } from 'src/lib/expirationTracking'
import {
  buildFullName,
  formatSnakeValue,
  formatBytes,
  formatDocumentCategory,
} from 'src/lib/formatters'

import BulkUploadDocumentReviewModal from './BulkUploadDocumentReviewModal'

const MEGABYTE_TO_BYTE = 1024 ** 2
const MEGABYTE_LIMIT = 100
const MAX_BULK_FILES = 20

const categories = buildDocumentCategoryOptions(formatDocumentCategory)

const statuses = [
  DocumentUserStatus.PENDING_REVIEW,
  DocumentUserStatus.COMPLETED,
]

const statusOptions = statuses.map((status) => ({
  label: formatSnakeValue(status),
  value: status,
}))

const fileContentTypeByFileType = {
  'image/png': FileContentType.PNG,
  'image/jpeg': FileContentType.JPG,
  'image/jpg': FileContentType.JPG,
  'image/tiff': FileContentType.TIF,
  'image/gif': FileContentType.GIF,
  'image/heic': FileContentType.HEIC,
  'application/pdf': FileContentType.PDF,
  'text/html': FileContentType.HTML,
  'application/msword': FileContentType.DOC,
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    FileContentType.DOC,
  'application/zip': FileContentType.ZIP,
  'text/csv': FileContentType.CSV,
  'text/plain': FileContentType.TXT,
  'audio/wav': FileContentType.WAV,
  'audio/x-wav': FileContentType.WAV,
  'video/mp4': FileContentType.MP4,
  'application/vnd.ms-excel': FileContentType.XLS,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
    FileContentType.XLS,
}

// ── AI state types ────────────────────────────────────────────────────────
// 'ai_none' — AI finished but produced nothing confident enough to fill. The
// modal stays open with empty fields, marked so the recruiter knows AI ran and
// abstained rather than failed (which would be 'ai_failed').
type AiPhase =
  | 'idle'
  | 'analyzing'
  | 'ai_high'
  | 'ai_low'
  | 'ai_none'
  | 'ai_failed'
type FieldAiInfo = { confidence: number }

// AI analyzing-phase timing. Long-wait must be < safety-close so the soft
// reassurance always appears before the modal force-closes itself.
const LONG_WAIT_THRESHOLD_MS = 10000
const ANALYZING_SAFETY_CLOSE_MS = 60000

// Small chip rendered inside field labels to show AI prediction provenance.
const AiChip = ({ info }: { info: FieldAiInfo }) => {
  const isHigh = info.confidence >= AI_HIGH_CONFIDENCE_THRESHOLD
  return (
    <span
      className="inline-flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] font-semibold leading-tight"
      style={{ backgroundColor: '#EBF5FB', color: '#1C93D7' }}
    >
      <span className="inline-flex">
        <IconSparkle size={8} />
      </span>
      {isHigh ? 'AI filled' : 'AI filled · review'}
    </span>
  )
}

// Gray sparkle shown beside a field AI reviewed but couldn't fill confidently.
// Gray (not blue "suggestion" or amber "warning") is the codebase's neutral AI
// color — it signals "AI ran and abstained" so an empty field doesn't read as
// an error or an unaddressed warning.
const AiAbstainedMarker = () => (
  <Tooltip label="AI unsure — no suggestion" withArrow position="top">
    <span
      className="inline-flex"
      style={{ color: '#9CA3AF', cursor: 'help' }}
      aria-label="AI reviewed, no confident suggestion"
    >
      <IconSparkle size={11} />
    </span>
  </Tooltip>
)

const AI_ANALYZING_MESSAGES = [
  'Uploading document…',
  'Analyzing document…',
  'Reading expiration dates…',
  'Categorizing document…',
  'Processing document fields…',
]

// Spinning sparkle with cycling messages for the loading phase.
// When `isLongWait` is true (the loading phase has exceeded LONG_WAIT_THRESHOLD_MS),
// cycling stops and a fixed message reassures the user they can save now and
// the upload + AI will continue in the background.
const AiAnalyzingBanner = ({
  isLongWait = false,
}: {
  isLongWait?: boolean
}) => {
  const [msgIndex, setMsgIndex] = useState(0)
  const [fading, setFading] = useState(false)

  useEffect(() => {
    if (isLongWait) return
    const interval = setInterval(() => {
      setFading(true)
      setTimeout(() => {
        setMsgIndex((i) => (i + 1) % AI_ANALYZING_MESSAGES.length)
        setFading(false)
      }, 350)
    }, 2200)
    return () => clearInterval(interval)
  }, [isLongWait])

  return (
    <div className="flex flex-col items-center gap-3 rounded-xl bg-doubleNickel-brand-25 px-4 py-5">
      <style>{`
        @keyframes sparkle-spin { to { transform: rotate(360deg); } }
        @keyframes sparkle-glow {
          0%, 100% { filter: drop-shadow(0 0 3px #1C93D760); }
          50% { filter: drop-shadow(0 0 10px #1C93D7AA); }
        }
        @keyframes ai-scan { 0% { left: -40%; } 100% { left: 140%; } }
      `}</style>
      <div
        style={{
          color: '#1C93D7',
          animation:
            'sparkle-spin 2.5s linear infinite, sparkle-glow 2s ease-in-out infinite',
          display: 'inline-flex',
        }}
      >
        <IconSparkle size={28} />
      </div>
      {isLongWait ? (
        <span
          className="text-center text-sm font-medium text-doubleNickel-brand-700"
          style={{ minHeight: '1.25rem', lineHeight: 1.4 }}
        >
          AI is taking longer than usual.
          <br />
          You can <strong>Save now</strong> — we&apos;ll keep working in the
          background.
        </span>
      ) : (
        <span
          className="text-sm font-medium text-doubleNickel-brand-700"
          style={{
            transition: 'opacity 0.35s ease, transform 0.35s ease',
            opacity: fading ? 0 : 1,
            transform: fading ? 'translateY(-5px)' : 'translateY(0)',
            minHeight: '1.25rem',
            textAlign: 'center',
          }}
        >
          {AI_ANALYZING_MESSAGES[msgIndex]}
        </span>
      )}
      <div className="relative h-1 w-full overflow-hidden rounded-full bg-doubleNickel-brand-100">
        <div
          className="absolute h-full w-2/5 rounded-full bg-doubleNickel-brand-400"
          style={{ animation: 'ai-scan 1.6s ease-in-out infinite' }}
        />
      </div>
    </div>
  )
}

// Banner shown at the top of the post-upload form for each AI phase.
const AiBanner = ({
  phase,
  filledCount,
  isLongWait = false,
}: {
  phase: AiPhase
  filledCount?: number
  isLongWait?: boolean
}) => {
  if (phase === 'analyzing') {
    return <AiAnalyzingBanner isLongWait={isLongWait} />
  }
  if (phase === 'ai_high') {
    return (
      <div className="flex items-center gap-2 rounded-lg bg-doubleNickel-brand-25 px-3 py-2.5 text-sm text-doubleNickel-brand-700">
        <span style={{ color: '#1C93D7', flexShrink: 0 }}>
          <IconSparkle size={13} />
        </span>
        <span>
          AI filled {filledCount ?? 0} field{filledCount !== 1 ? 's' : ''}.
          Review and save, or edit any field.
        </span>
      </div>
    )
  }
  if (phase === 'ai_low') {
    return (
      <div
        className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm"
        style={{ backgroundColor: '#EBF5FB', color: '#115881' }}
      >
        <span className="shrink-0" style={{ color: '#1C93D7' }}>
          <IconSparkle size={13} />
        </span>
        <span>AI confidence is low. Please confirm before saving.</span>
      </div>
    )
  }
  if (phase === 'ai_failed') {
    return (
      <div className="rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-700">
        Something went wrong while analyzing this document.
      </div>
    )
  }
  return null
}

const SelectedFile = ({
  file,
  onClose,
}: {
  file: File | null
  onClose?: () => void
}) => {
  if (!file) return null
  return (
    <div className="flex flex-row items-baseline gap-3 rounded bg-doubleNickel-gray-50 p-2">
      <File size={12} className="stroke-doubleNickel-gray-500" />
      <div className="flex flex-col items-start justify-start">
        <Text size="sm" fw={500}>
          {file.name}
        </Text>
        <Text size="xs" fw={200}>
          {formatBytes(file.size)}
        </Text>
      </div>
      {onClose && (
        <CloseButton onClick={onClose} size="xs" className="ml-auto" />
      )}
    </div>
  )
}

export const appendCorrectFileExtension = (fileName, mimeType) => {
  const extensionsByMimeType = {
    'image/png': '.png',
    'image/jpeg': '.jpg',
    'application/pdf': '.pdf',
  }

  const extension = extensionsByMimeType[mimeType]
  if (!extension) return fileName // No extension found for MIME type

  const fileNameParts = fileName.split('.')
  const currentExtension =
    fileNameParts.length > 1 ? '.' + fileNameParts.pop() : ''

  // If the current extension is correct, return the filename as is
  if (currentExtension.toLowerCase() === extension.toLowerCase()) {
    return fileName
  }

  // Append the correct extension
  return fileName + extension
}

export interface UploadDocumentModalProps {
  applicantId: string
  isOpen: boolean
  onClose: () => void
  onRender
  onUpdateComplete?: (document) => void
  step: Step
  voe: VerificationOfEmployment
  showExpirationDate?: boolean
  disableCategorySelection?: boolean
  onBulkUploadStart?: () => void
}

const UploadDocumentModal = ({
  applicantId,
  isOpen,
  onClose,
  onRender,
  onUpdateComplete,
  step,
  voe,
  showExpirationDate = true,
  disableCategorySelection = false,
  onBulkUploadStart,
}: UploadDocumentModalProps) => {
  const form = useForm({
    initialValues: {
      category: voe ? DocumentCategory.VERIFICATION_OF_EMPLOYMENT : '',
      // Default to Pending Review for all uploads. Recruiters explicitly flip
      // to Completed once they've reviewed the file (and any AI suggestions);
      // defaulting to Completed risked auto-finishing checklist steps before
      // the file was actually verified.
      userStatus: DocumentUserStatus.PENDING_REVIEW,
      fileName: '',
      expirationDate: null,
      file: null,
    },

    validate: {
      category: (value) => (value ? null : 'Category is required'),
      userStatus: (value) => (value ? null : 'Status is required'),
      file: (value) => (value ? null : 'File is required'),
    },
  })

  const currentCompany = useCurrentCompanyContext()
  const aiEnabled = currentCompany?.documentIntelligenceEnabled ?? false
  // A VoE-bound upload has a structurally fixed category
  // (VERIFICATION_OF_EMPLOYMENT) and the backend skips classification for it
  // entirely (ENG-4403), so the client skips the AI flow too: no
  // upload-on-drop, no "Categorizing document…" banner, no auto-close. The
  // recruiter reviews the pre-filled VOE category and saves like a plain
  // (non-AI) upload.
  const aiFlowEnabled = aiEnabled && !voe

  const [isUploading, setIsUploading] = useState(false)
  // Completed completes an open VoE with the file; Pending Review only attaches it.
  const [voeStatus, setVoeStatus] = useState<DocumentUserStatus>(
    DocumentUserStatus.COMPLETED
  )
  const [voeCompletion, setVoeCompletion] =
    useState<VoECompletionValue>(EMPTY_VOE_COMPLETION)
  const [showVoECompletionErrors, setShowVoECompletionErrors] = useState(false)
  const isVoEAlreadyCompleted = voe?.status === VoEStatus.COMPLETED
  const isVoEResponse =
    !!voe &&
    voeStatus === DocumentUserStatus.COMPLETED &&
    !isVoEAlreadyCompleted
  // Read when the upload finishes: null attaches the file as Pending Review,
  // otherwise it is marked Completed (with the completion for an open VoE).
  // A ref, not state: onCompleted runs with the last rendered closure, and a
  // Cancel mid-upload must clear it.
  const pendingVoECompletionRef = useRef<{
    voeCompletion: ReturnType<typeof toVoECompletionInput> | null
  } | null>(null)
  const resetVoEFileState = () => {
    pendingVoECompletionRef.current = null
    setVoeStatus(DocumentUserStatus.COMPLETED)
    setVoeCompletion(EMPTY_VOE_COMPLETION)
    setShowVoECompletionErrors(false)
  }
  // The file whose upload is in flight. createDocument's onCompleted reads it
  // here, not from form.values: onCompleted runs with the last rendered
  // closure, and on the AI path the upload starts in the same tick as the
  // drop's setFieldValue, so a fast response can arrive before that render
  // and see no file (the upload then silently never happened). handleClose
  // clears it, which is how a Cancel / Save now mid-upload is detected.
  const uploadFileRef = useRef<File | null>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [showBulkReview, setShowBulkReview] = useState(false)
  const {
    startBulkDocumentUpload,
    updateFileUploadProgress,
    completeBulkDocumentUpload,
  } = useBulkUploadDocumentProgress()

  // Object URL for in-modal document preview (revoked on cleanup)
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!form.values.file) {
      setFilePreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(form.values.file)
    setFilePreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [form.values.file])

  // ── AI state ────────────────────────────────────────────────────────────
  const [aiPhase, setAiPhase] = useState<AiPhase>('idle')
  const [uploadedDocId, setUploadedDocId] = useState<string | null>(null)
  const [aiFieldInfo, setAiFieldInfo] = useState<{
    category?: FieldAiInfo
    expirationDate?: FieldAiInfo
  } | null>(null)
  // Raw predicted values — used to detect if user kept or changed AI suggestions on save
  const [aiPredictedValues, setAiPredictedValues] = useState<{
    category?: string | null
    expirationDate?: Date | null
  } | null>(null)
  // Fields where AI had a prediction but it fell below the display-confidence
  // threshold, so nothing was filled. Drives the gray "AI reviewed" marker in
  // the 'ai_none' phase. Analytics excludes these sub-threshold predictions from
  // "awaiting review" at the query layer (documentIntelligenceAnalytics), so the
  // client doesn't need to record any review decision for them.
  const [aiAbstainedFields, setAiAbstainedFields] = useState<{
    category?: boolean
    expirationDate?: boolean
  }>({})
  // Flips to true after LONG_WAIT_THRESHOLD_MS in the loading phase, softening banner copy.
  const [isLongWait, setIsLongWait] = useState(false)

  // True while the file is uploading to S3 OR while AI is analyzing.
  // Gated on aiFlowEnabled so non-AI users and VoE uploads never see the AI
  // analyzing banner or skeletons.
  // Hoisted up here so the long-wait effect below can depend on it.
  const isLoadingPhase =
    aiFlowEnabled && (isUploading || aiPhase === 'analyzing')

  // Poll document intelligence status after upload completes
  const {
    data: intelligenceData,
    startPolling,
    stopPolling,
  } = useQuery(GET_DOCUMENT_INTELLIGENCE, {
    variables: { id: uploadedDocId ?? '', applicantId },
    skip: !uploadedDocId,
    fetchPolicy: 'network-only',
  })

  // Activate polling while in analyzing phase
  useEffect(() => {
    if (aiPhase === 'analyzing' && uploadedDocId) {
      startPolling(2000)
    } else {
      stopPolling()
    }
    return () => stopPolling()
  }, [aiPhase, uploadedDocId, startPolling, stopPolling])

  // Safety timeout: close modal if AI never responds (e.g., not enabled for company)
  useEffect(() => {
    if (aiPhase !== 'analyzing') return
    const timeout = setTimeout(() => {
      stopPolling()
      handleClose()
      toast('Document created successfully', 'success')
    }, ANALYZING_SAFETY_CLOSE_MS)
    return () => clearTimeout(timeout)
  }, [aiPhase])

  // Long-wait threshold: soften the banner copy partway through the loading phase.
  // Tied to isLoadingPhase (not aiPhase) so the 10s timer counts wall-clock time
  // from when the spinner first appeared, including slow S3 uploads on 4G/3G.
  // The upload→analyzing transition keeps isLoadingPhase=true, so the timer doesn't reset.
  useEffect(() => {
    if (!isLoadingPhase) {
      setIsLongWait(false)
      return
    }
    const timeout = setTimeout(
      () => setIsLongWait(true),
      LONG_WAIT_THRESHOLD_MS
    )
    return () => clearTimeout(timeout)
  }, [isLoadingPhase])

  // Process intelligence results when polling returns data
  useEffect(() => {
    if (aiPhase !== 'analyzing') return
    const doc = intelligenceData?.document
    const intel = doc?.documentIntelligence
    if (!intel) return

    if (intel.status === 'COMPLETED') {
      stopPolling()

      const expirationPred = intel.predictions.find((p) => p.field === 'DATE')
      const categoryPred = intel.predictions.find((p) => p.field === 'CATEGORY')

      const newFieldInfo: {
        category?: FieldAiInfo
        expirationDate?: FieldAiInfo
      } = {}
      const newPredictedValues: {
        category?: string | null
        expirationDate?: Date | null
      } = {}
      // Fields AI ran on but returned no confident prediction for. Sub-threshold
      // predictions are dropped by the backend, so an ABSENT prediction means AI
      // wasn't confident — tracked so the modal can explain the empty field
      // ("AI was unsure") rather than the field silently vanishing.
      const newAbstainedFields: {
        category?: boolean
        expirationDate?: boolean
      } = {}
      let anyHighConfidence = false

      if (expirationPred && expirationPred.expirationValue) {
        newFieldInfo.expirationDate = { confidence: expirationPred.confidence }
        // The stored value is a UTC-midnight instant. Rebuild as local midnight
        // on the same calendar day so the DatePicker highlights the correct cell.
        const utcDate = new Date(expirationPred.expirationValue)
        const predictedDate = new Date(
          utcDate.getUTCFullYear(),
          utcDate.getUTCMonth(),
          utcDate.getUTCDate()
        )
        newPredictedValues.expirationDate = predictedDate
        form.setFieldValue('expirationDate', predictedDate)
        if (expirationPred.confidence >= AI_HIGH_CONFIDENCE_THRESHOLD)
          anyHighConfidence = true
      } else if (
        !expirationPred &&
        showExpirationDate &&
        !form.values.expirationDate
      ) {
        // AI ran but produced no expiration prediction at all. Sub-threshold
        // predictions are dropped by the backend, so a fully absent prediction
        // means AI wasn't confident. Only mark it when the field is shown and
        // still empty: a present prediction with no value means AI was confident
        // there's no date (not unsure — handled by the no-op above), and a date
        // the recruiter already entered shouldn't be second-guessed. Matches
        // DocumentEditScreen's getChipState.
        newAbstainedFields.expirationDate = true
      }

      if (categoryPred && categoryPred.categoryValue) {
        // Always record the prediction so a disagreement is reported to
        // reviewDocumentPredictions as KEPT_ORIGINAL below — that override
        // signal is how we find document types DI misreads (ENG-4638).
        newPredictedValues.category = categoryPred.categoryValue
        if (!disableCategorySelection) {
          // Pre-fill the suggested category so the recruiter can see and confirm it
          newFieldInfo.category = { confidence: categoryPred.confidence }
          form.setFieldValue('category', categoryPred.categoryValue)
          if (categoryPred.confidence >= AI_HIGH_CONFIDENCE_THRESHOLD)
            anyHighConfidence = true
        }
        // When the category is locked to a checklist item the recruiter already
        // chose it by picking that item, so no chip is shown and the prediction
        // never counts toward anyHighConfidence — nothing was AI-filled.
      } else if (
        !categoryPred &&
        !disableCategorySelection &&
        !form.values.category
      ) {
        // AI ran but produced no category prediction at all (sub-threshold
        // predictions are dropped by the backend, so absence means AI wasn't
        // confident). Only mark it when the category field is editable and still
        // empty — when category is locked from the hiring step
        // (disableCategorySelection) or already chosen (recruiter/voe/step
        // preset), AI was never expected to fill it. A present prediction with
        // no value is AI confidently classifying nothing (not unsure), handled
        // by the no-op above. Matches DocumentEditScreen's getChipState.
        newAbstainedFields.category = true
      }

      // Record abstained fields on every path so the gray marker also appears
      // when one field was confidently filled (ai_high/ai_low) while the other
      // fell below threshold — not only on the all-empty 'ai_none' path.
      setAiAbstainedFields(newAbstainedFields)

      // Record predictions before any early return. A locked category still
      // produces a prediction that has to reach reviewDocumentPredictions even
      // though it fills nothing on screen (ENG-4638), so this can't wait until
      // the AI review screen is reached.
      setAiPredictedValues(newPredictedValues)

      // Nothing was confidently filled. If AI did extract values but they fell
      // below the display threshold, keep the modal open in the 'ai_none' phase
      // so the recruiter sees the empty fields are intentional (AI abstained),
      // not a glitch. If AI extracted nothing at all, close normally as before.
      if (!newFieldInfo.expirationDate && !newFieldInfo.category) {
        if (newAbstainedFields.category || newAbstainedFields.expirationDate) {
          // The modal stays open and Save still runs handleSaveAiValues, which
          // reports the decision from aiPredictedValues set above.
          setAiPhase('ai_none')
          return
        }
        // The modal closes without a Save, so report the category decision here
        // or the override signal is lost entirely.
        reportCategoryDecision(newPredictedValues.category)
        handleClose()
        toast('Document created successfully', 'success')
        return
      }

      setAiFieldInfo(newFieldInfo)
      // Reaching the AI review screen IS the recruiter's review pass — they're
      // about to look at the AI-filled fields and click Save. Pre-flip the
      // Status dropdown to Completed so the visible value matches what Save
      // will persist. The recruiter can still flip back to Pending Review if
      // the AI suggestions need a second look, and the expiration-date guard
      // in handleSaveAiValues still forces PENDING_REVIEW if a required date
      // is missing.
      form.setFieldValue('userStatus', DocumentUserStatus.COMPLETED)
      setAiPhase(anyHighConfidence ? 'ai_high' : 'ai_low')
    } else if (intel.status === 'FAILED') {
      stopPolling()
      setAiPhase('ai_failed')
    }
  }, [intelligenceData, aiPhase])

  // Reset AI state when modal is closed externally (isOpen prop changes to false)
  useEffect(() => {
    if (!isOpen) {
      stopPolling()
      setAiPhase('idle')
      setUploadedDocId(null)
      setAiFieldInfo(null)
      setAiPredictedValues(null)
      setAiAbstainedFields({})
      setIsLongWait(false)
      // The modal stays mounted between opens (VoEDocumentsSection), so the
      // next VoE upload starts from the default choice, not the last one.
      resetVoEFileState()
    }
  }, [isOpen])

  // Save without waiting for AI — document is already stored, just close.
  const handleSaveNow = () => {
    handleClose()
    toast(
      'Document saved. Review AI suggestions in the document view.',
      'success'
    )
  }

  const handleClose = () => {
    stopPolling()
    setAiPhase('idle')
    setUploadedDocId(null)
    setAiFieldInfo(null)
    setAiPredictedValues(null)
    setAiAbstainedFields({})
    setIsLongWait(false)
    setIsUploading(false)
    resetVoEFileState()
    uploadFileRef.current = null
    form.reset()
    onClose()
  }

  const onError = () => {
    // Symmetric to the createDocument.onCompleted guard: if the user closed
    // the modal (Cancel / Save now), the form was reset and file is null.
    // Suppress the error toast — it would contradict the optimistic
    // "Document saved" toast Save now showed (or jarringly appear after an
    // explicit Cancel). The orphaned DB record is a known issue tracked by
    // the existing footer TODO. setIsUploading is already false from
    // handleClose, so nothing left to do here.
    if (!uploadFileRef.current) return
    toast('Unable to create/update document, please try again.', 'error')
    setIsUploading(false)
  }

  useEffect(() => {
    if (step && step.type) {
      // Use document category conversion or default to step type
      const stepDocumentCategory =
        stepTypeToDocumentCategoryMapping[step.type] || step.type
      const categoryValue =
        categories.find((cat) => cat.value === stepDocumentCategory)?.value ??
        DocumentCategory.OTHER
      form.setFieldValue('category', categoryValue)
    }
  }, [step])

  // Re-assert on every open, not just when `voe` changes: this modal stays
  // mounted in VoEDocumentsSection, so `initialValues.category` can be '' when
  // `voe` hadn't loaded yet at mount time — after handleClose's form.reset()
  // the next open would show a blank (and locked) category without this.
  useEffect(() => {
    if (isOpen && voe) {
      form.setFieldValue(
        'category',
        DocumentCategory.VERIFICATION_OF_EMPLOYMENT
      )
    }
  }, [voe, isOpen])

  // Marks upload complete. If AI is enabled for this company, transitions to
  // the analyzing state. Otherwise restores the original close-with-toast flow.
  const [completeUpload] = useMutation(UPDATE_DOCUMENT, {
    refetchQueries: onRender,
    awaitRefetchQueries: true,
    onCompleted: ({ updateDocument: doc }) => {
      setIsUploading(false)
      if (onUpdateComplete) onUpdateComplete(doc)
      if (aiFlowEnabled) {
        setUploadedDocId(doc.documentId)
        setAiPhase('analyzing')
      } else {
        const completedVoE = !!pendingVoECompletionRef.current?.voeCompletion
        pendingVoECompletionRef.current = null
        uploadFileRef.current = null
        form.reset()
        onClose()
        toast(
          completedVoE
            ? 'Response uploaded. Verification completed.'
            : 'Document created successfully',
          'success'
        )
      }
    },
    onError,
  })

  // Saves recruiter-confirmed values (category / expiration) after AI phase
  const [saveDocumentFields, { loading: isSavingAi }] = useMutation(
    UPDATE_DOCUMENT,
    {
      refetchQueries: onRender,
      onCompleted: () => {
        handleClose()
        toast('Document saved successfully', 'success')
      },
      onError: () =>
        toast('Unable to save document, please try again.', 'error'),
    }
  )

  const [reviewDocumentPredictions] = useMutation(REVIEW_DOCUMENT_PREDICTIONS)

  const [createDocument] = useMutation(CREATE_DOCUMENT, {
    onCompleted: async ({ createDocument }) => {
      const { uploadUrl } = createDocument
      // If the user closed the modal (Cancel / Save now) while CREATE_DOCUMENT
      // was in flight, handleClose has already cleared the in-flight file.
      // Bail silently — the DB record is orphaned but no S3 upload is attempted,
      // and we avoid throwing a "Document upload failed" toast that would
      // contradict the success toast Save now just showed.
      const file = uploadFileRef.current
      if (!file) return

      try {
        const response = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        })

        if (!response.ok) {
          throw new Error(`Upload failed with status: ${response.status}`)
        }

        // Completed only once the file is stored, so a failed upload never
        // completes the VoE.
        const voeUpload = pendingVoECompletionRef.current
        completeUpload({
          variables: {
            id: createDocument.documentId,
            input: {
              uploadStatus: UploadStatus.COMPLETED,
              ...(voeUpload && {
                userStatus: DocumentUserStatus.COMPLETED,
                ...(voeUpload.voeCompletion && {
                  voeCompletion: voeUpload.voeCompletion,
                }),
              }),
            },
          },
        })
      } catch (error) {
        console.error('Document upload to S3 failed:', error)
        // If the user closed the modal mid-fetch (Cancel / Save now), the
        // captured `file` kept the upload alive but uploadFileRef is now
        // null. Suppress the toast so it doesn't contradict the optimistic
        // "Document saved" toast Save now showed. setIsUploading is already
        // false from handleClose. Same race-class as the onCompleted/onError
        // guards above, just on the S3 leg.
        if (!uploadFileRef.current) return
        toast('Document upload failed. Please try again.', 'error')
        setIsUploading(false)
      }
    },
    onError,
  })

  const onFormSubmit = (values) => {
    const { file, fileName, expirationDate, ...createDocumentInput } = values
    // The employer's response completes the VoE, so it needs to say how the
    // answer arrived (and, for ATS, through which platform) before it saves.
    if (isVoEResponse && !isVoECompletionValid(voeCompletion)) {
      setShowVoECompletionErrors(true)
      return
    }
    const updatedFileName = fileName || file.name
    const fileNameWithExtension = appendCorrectFileExtension(
      updatedFileName,
      file.type
    )
    pendingVoECompletionRef.current =
      voe && voeStatus === DocumentUserStatus.COMPLETED
        ? {
            voeCompletion: isVoEResponse
              ? toVoECompletionInput(voeCompletion)
              : null,
          }
        : null
    uploadFileRef.current = file
    setIsUploading(true)
    createDocument({
      variables: {
        input: {
          ...createDocumentInput,
          fileType: fileContentTypeByFileType[file.type],
          fileName: fileNameWithExtension,
          expirationDate: expirationDate ? new Date(expirationDate) : null,
          applicantId,
          stepId: step ? step.stepId : null,
          voeId: voe ? voe.voeId : null,
          // Created as Pending Review; see createDocument.onCompleted.
          ...(voe && { userStatus: DocumentUserStatus.PENDING_REVIEW }),
        },
      },
    })
  }

  // Reports the CATEGORY decision on its own, for the path where AI predicted a
  // category but nothing reached the review screen, so no Save (and therefore no
  // handleSaveAiValues) will ever run. Without this the override that ENG-4638
  // exists to capture is silently dropped for step-locked categories.
  const reportCategoryDecision = (predictedCategory?: string | null) => {
    if (!uploadedDocId || !predictedCategory) return
    reviewDocumentPredictions({
      variables: {
        documentId: uploadedDocId,
        decisions: [
          {
            field: 'CATEGORY',
            decision:
              form.values.category === predictedCategory
                ? 'ACCEPTED_AI'
                : 'KEPT_ORIGINAL',
          },
        ],
      },
    })
  }

  // Saves the current form values back to the already-uploaded document.
  // For any AI-suggested field the recruiter did NOT accept, mark it KEPT_ORIGINAL
  // so the individual document view won't prompt them to review it again.
  const handleSaveAiValues = () => {
    const validation = form.validate()
    if (validation.hasErrors) return

    // Not gated on aiFieldInfo: a locked category predicts without filling any
    // field, so aiFieldInfo is empty while aiPredictedValues still holds the
    // override to report. The per-field checks below guard on it individually.
    if (uploadedDocId && aiPredictedValues) {
      const decisions: { field: string; decision: string }[] = []

      if (
        aiFieldInfo?.expirationDate != null &&
        aiPredictedValues.expirationDate != null
      ) {
        const saved = form.values.expirationDate
        const predicted = aiPredictedValues.expirationDate
        const accepted =
          saved != null &&
          new Date(saved).toDateString() === new Date(predicted).toDateString()
        decisions.push({
          field: 'DATE',
          decision: accepted ? 'ACCEPTED_AI' : 'KEPT_ORIGINAL',
        })
      }

      if (aiPredictedValues.category != null) {
        const accepted = form.values.category === aiPredictedValues.category
        decisions.push({
          field: 'CATEGORY',
          decision: accepted ? 'ACCEPTED_AI' : 'KEPT_ORIGINAL',
        })
      }

      if (decisions.length > 0) {
        reviewDocumentPredictions({
          variables: { documentId: uploadedDocId, decisions },
        })
      }
    }

    saveDocumentFields({
      variables: {
        id: uploadedDocId!,
        input: {
          category: form.values.category as DocumentCategory,
          expirationDate: form.values.expirationDate
            ? new Date(form.values.expirationDate)
            : null,
          // If the expiration date field is visible but empty for a category
          // that tracks expiration, the AI missed it and the recruiter hasn't
          // filled it in — force PENDING_REVIEW so the checklist item doesn't
          // auto-complete without a valid date. Otherwise preserve
          // form.values.userStatus, which is PENDING_REVIEW by default but
          // may be COMPLETED if the recruiter explicitly flipped the Status
          // select. Non-tracked categories (e.g. BACKGROUND_CHECK, W9) don't
          // need expiration dates so the recruiter's choice is preserved.
          userStatus:
            !form.values.expirationDate &&
            showExpirationDate &&
            TRACKED_EXPIRATION_CATEGORIES.includes(
              form.values.category as DocumentCategory
            )
              ? DocumentUserStatus.PENDING_REVIEW
              : (form.values.userStatus as DocumentUserStatus),
          ...(form.values.fileName && { fileName: form.values.fileName }),
        },
      },
    })
  }

  const onDrop = (files) => {
    // Bulk upload: if more than 1 file, show bulk review modal
    if (files.length > 1) {
      setSelectedFiles(files)
      setShowBulkReview(true)
      return
    }

    // Single file: use existing flow
    const file = files[0]
    form.setFieldValue('file', file)

    // When the AI flow applies, start the upload immediately on drop so the
    // loading animation plays before the user touches any form field.
    if (aiFlowEnabled) {
      const fileNameWithExtension = appendCorrectFileExtension(
        file.name,
        file.type
      )
      form.setFieldValue('fileName', fileNameWithExtension)
      const category =
        (form.values.category as DocumentCategory) || DocumentCategory.OTHER
      uploadFileRef.current = file
      setIsUploading(true)
      createDocument({
        variables: {
          input: {
            category,
            userStatus:
              (form.values.userStatus as DocumentUserStatus) ||
              DocumentUserStatus.PENDING_REVIEW,
            fileType: fileContentTypeByFileType[file.type],
            fileName: fileNameWithExtension,
            expirationDate: null,
            applicantId,
            stepId: step ? step.stepId : null,
            voeId: voe ? voe.voeId : null,
          },
        },
      })
    }
  }

  const onReject = (fileRejections) => {
    fileRejections.forEach((rejection) => {
      if (rejection.errors[0].code === 'file-too-large') {
        toast(
          `File size exceeds the maximum limit of ${MEGABYTE_LIMIT}MB`,
          'error'
        )
      } else if (rejection.errors[0].code === 'too-many-files') {
        toast(
          `Bulk Document Upload supports up to ${MAX_BULK_FILES} files`,
          'error'
        )
      } else {
        toast(`Unknown error occurred: ${rejection.errors[0].code}`, 'error')
      }
    })
  }

  const cleanSelectedFile = () => {
    form.setFieldValue('file', null)
  }

  const handleBulkUploadSubmit = (data: {
    category: DocumentCategory
    userStatus: DocumentUserStatus
    expirationDate: Date | null
  }) => {
    const filesToUpload = selectedFiles
    setShowBulkReview(false)
    setSelectedFiles([])
    startBulkDocumentUpload(filesToUpload)
    onBulkUploadStart?.()
    handleClose()

    // Upload files in background without blocking the UI
    const uploadNextFile = async (fileIndex: number) => {
      if (fileIndex >= filesToUpload.length) {
        completeBulkDocumentUpload()
        return
      }

      const file = filesToUpload[fileIndex]
      updateFileUploadProgress(fileIndex, 'uploading')

      try {
        const fileNameWithExtension = appendCorrectFileExtension(
          file.name,
          file.type
        )
        const fileContentType = fileContentTypeByFileType[file.type]

        // Create document record
        const createDocResponse = await new Promise<{
          createDocument: { uploadUrl: string; documentId: string }
        }>((resolve, reject) => {
          createDocument({
            variables: {
              input: {
                category: data.category,
                // Every file in a multi-file drop from a VoE is a supporting
                // file; the response has to be uploaded on its own.
                userStatus: voe
                  ? DocumentUserStatus.PENDING_REVIEW
                  : data.userStatus,
                fileType: fileContentType,
                fileName: fileNameWithExtension,
                expirationDate: data.expirationDate
                  ? new Date(data.expirationDate)
                  : null,
                applicantId,
                stepId: step ? step.stepId : null,
                voeId: voe ? voe.voeId : null,
              },
            },
            onCompleted: resolve,
            onError: reject,
          })
        })

        const { uploadUrl, documentId } = createDocResponse.createDocument

        // Upload to S3
        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type,
          },
        })

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed with status: ${uploadResponse.status}`)
        }

        // Mark upload as completed
        await new Promise<void>((resolve, reject) => {
          completeUpload({
            variables: {
              id: documentId,
              input: {
                uploadStatus: UploadStatus.COMPLETED,
              },
            },
            onCompleted: () => resolve(),
            onError: reject,
          })
        })

        updateFileUploadProgress(fileIndex, 'completed')
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        updateFileUploadProgress(fileIndex, 'failed', errorMessage)
      }

      // Upload next file
      uploadNextFile(fileIndex + 1)
    }

    // Start uploading files in background
    uploadNextFile(0)
  }

  const isPostUpload = aiPhase !== 'idle'
  // Locked when the upload context dictates the category (DQF step slot).
  // Post-upload the lock lifts so the recruiter can still re-categorize during
  // review (#3097). What ENG-4638 actually had to stop was AI *pre-filling* the
  // suggestion into the field, which made Save persist it by default and moved
  // the document off the checklist item just uploaded into; that is handled at
  // the prediction site, which skips setFieldValue when the category is locked.
  // Keeping the field disabled here would only take the override away from the
  // recruiter. VOE uploads likewise pre-fill but stay editable.
  const isCategoryLocked = disableCategorySelection && !isPostUpload
  // Show wide side-by-side layout when AI results are ready and the file is previewable.
  const previewableMimeTypes = new Set<string>([
    ...PDF_MIME_TYPE,
    ...IMAGE_MIME_TYPE,
  ])
  const isPdfPreview = form.values.file?.type === 'application/pdf'
  const hasPreview =
    isPostUpload &&
    !isLoadingPhase &&
    !!filePreviewUrl &&
    !!form.values.file &&
    previewableMimeTypes.has(form.values.file.type)
  // Not gated on filePreviewUrl (set an effect later), so the form doesn't
  // remount when the layout widens.
  const hasVoEPreview =
    !!voe &&
    !isPostUpload &&
    !!form.values.file &&
    previewableMimeTypes.has(form.values.file.type)
  const voeApplicantName = voe
    ? buildFullName(voe.applicant) || voe.driverName
    : null
  // Count only auto-applied (high-confidence) predictions for the "AI filled N fields" banner.
  // Low-confidence predictions are surfaced for review but weren't actually filled by AI.
  const aiFilledCount = aiFieldInfo
    ? Object.values(aiFieldInfo).filter(
        (info) =>
          info != null && info.confidence >= AI_HIGH_CONFIDENCE_THRESHOLD
      ).length
    : 0

  // Inline label nodes that include AI chips for affected fields.
  // Use inline-flex (not flex) so Mantine's `withAsterisk` red `*` stays on the
  // same line as the label. flex-shrink-0 on the chip keeps it from being squeezed
  // when the label container is narrow (e.g. the w-72 right-side panel).
  const categoryLabel = (
    <span className="inline-flex items-center gap-x-1.5">
      Category
      {aiFieldInfo?.category && <AiChip info={aiFieldInfo.category} />}
      {aiAbstainedFields.category && <AiAbstainedMarker />}
    </span>
  )

  const expirationLabel = (
    <span className="inline-flex items-center gap-x-1.5">
      Expiration Date
      {aiFieldInfo?.expirationDate && (
        <AiChip info={aiFieldInfo.expirationDate} />
      )}
      {aiAbstainedFields.expirationDate && <AiAbstainedMarker />}
    </span>
  )

  // The category is fixed for a VoE upload, so it is not shown.
  const voeFormFields = voe && (
    <div className="flex flex-col gap-4">
      <ReadOnlyField label="Applicant Name" value={voeApplicantName || '—'} />
      <ReadOnlyField label="Company Name" value={voe.companyName || '—'} />
      <SelectedFile
        file={form.values.file}
        onClose={isLoadingPhase || isPostUpload ? undefined : cleanSelectedFile}
      />
      <TextInput
        placeholder={form.values.file?.name ?? 'Enter a file name'}
        label="File Name"
        disabled={isLoadingPhase}
        {...form.getInputProps('fileName')}
      />
      <Select
        withAsterisk
        label="Status"
        className="max-w-[220px]"
        data={statusOptions}
        value={voeStatus}
        allowDeselect={false}
        disabled={isUploading}
        onChange={(value) => {
          if (!value) return
          setVoeStatus(value as DocumentUserStatus)
          setShowVoECompletionErrors(false)
        }}
      />
      {isVoEResponse && (
        <VoECompletionFields
          value={voeCompletion}
          onChange={setVoeCompletion}
          subject={voe}
          defaultPlatform={defaultVoECompletionPlatform(voe)}
          allowNoResponse={false}
          showErrors={showVoECompletionErrors}
          disabled={isUploading}
        />
      )}
    </div>
  )

  const footer = (
    <>
      <Divider variant="dashed" className="my-6" />
      <div className="flex flex-row gap-3">
        {isLoadingPhase ? (
          <>
            {/* TODO: Cancel should also delete the uploaded document. Today
              Cancel and Save now both close the modal and let background
              upload + AI continue — copy differentiates intent. */}
            <DNButton
              className="flex-1"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </DNButton>
            {/* Once long-wait kicks in, elevate Save now to primary so the
              user's eye is drawn to the action they're now being invited to take. */}
            <DNButton
              className="flex-1 transition-colors duration-300"
              variant={isLongWait ? 'primary' : 'outline'}
              onClick={handleSaveNow}
            >
              Save now
            </DNButton>
            <DNButton className="flex-1" variant="primary" disabled loading>
              Analyzing…
            </DNButton>
          </>
        ) : isPostUpload ? (
          <>
            <DNButton
              className="flex-1"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </DNButton>
            <DNButton
              className="flex-1"
              variant="primary"
              onClick={handleSaveAiValues}
              loading={isSavingAi}
              disabled={isSavingAi}
            >
              Save
            </DNButton>
          </>
        ) : (
          <>
            <DNButton
              className="flex-1"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </DNButton>
            <DNButton
              className="flex-1"
              type="submit"
              disabled={!form.isValid() || isUploading}
              variant="primary"
            >
              {voe
                ? isVoEResponse
                  ? 'Upload and complete'
                  : 'Upload'
                : 'Save'}
            </DNButton>
          </>
        )}
      </div>
    </>
  )

  const previewPane = (
    <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-gray-50">
      {!filePreviewUrl ? null : isPdfPreview ? (
        <iframe
          src={filePreviewUrl}
          title="Document preview"
          className="h-full w-full rounded-lg"
          style={{ border: 'none' }}
        />
      ) : (
        <img
          src={filePreviewUrl}
          alt="Document preview"
          className="h-full w-full rounded-lg object-contain p-2"
        />
      )}
    </div>
  )

  // Only call onFormSubmit in idle state; block while uploading or in post-upload states
  const handleFormSubmit =
    aiPhase === 'idle' && !isUploading
      ? form.onSubmit((values) => onFormSubmit(values))
      : (e: React.FormEvent) => e.preventDefault()

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={handleClose}
        classNames={{ title: 'font-semibold' }}
        title="Upload a document"
        centered
        size={
          hasPreview
            ? 'clamp(600px, 95vw, 1400px)'
            : hasVoEPreview
            ? 'clamp(600px, 95vw, 1200px)'
            : undefined
        }
        stackId="upload-document-modal"
      >
        <div className="flex flex-col gap-6">
          <form onSubmit={handleFormSubmit}>
            {/* ── Dropzone: shown only when no file selected and idle ── */}
            {!form.values.file && !isPostUpload && !showBulkReview && (
              <div className="py-4">
                <Dropzone
                  accept={[
                    ...IMAGE_MIME_TYPE,
                    ...PDF_MIME_TYPE,
                    ...MS_WORD_MIME_TYPE,
                    ...MS_EXCEL_MIME_TYPE,
                  ]}
                  maxFiles={MAX_BULK_FILES}
                  multiple={true}
                  maxSize={MEGABYTE_LIMIT * MEGABYTE_TO_BYTE}
                  onDrop={onDrop}
                  onReject={onReject}
                  {...form.getInputProps('file')}
                >
                  <Group className="flex flex-col">
                    <Dropzone.Accept>
                      <p>Drag and drop the file here</p>
                    </Dropzone.Accept>
                    <Dropzone.Reject>
                      <p>File does not meet requirements</p>
                    </Dropzone.Reject>
                    <Dropzone.Idle>
                      <ActionIcon
                        variant="subtle"
                        className="h-10 w-10 rounded-lg border border-doubleNickel-gray-200 bg-doubleNickel-white shadow-sm"
                      >
                        <IconUpload className="fill-none stroke-doubleNickel-gray-700" />
                      </ActionIcon>
                    </Dropzone.Idle>
                    <div className="flex flex-col items-center gap-1">
                      <Text className="text-sm text-doubleNickel-gray-600">
                        <span className="font-semibold text-doubleNickel-brand-500">
                          {'Click to upload '}
                        </span>
                        or drag and drop
                      </Text>
                      <Text
                        size="sm"
                        className="text-xs text-doubleNickel-gray-600"
                      >
                        PDF, PNG, JPG, JPEG, XLS or XLSX (Max {MEGABYTE_LIMIT}
                        MB, up to {MAX_BULK_FILES} files)
                      </Text>
                    </div>
                  </Group>
                </Dropzone>
              </div>
            )}

            {/* ── Post-upload: side-by-side document preview + AI form ── */}
            {hasPreview && (
              <div
                className="flex gap-4"
                style={{ height: 'min(780px, 80vh)' }}
              >
                {/* Document preview pane */}
                {previewPane}

                {/* AI form panel */}
                <div className="flex w-72 flex-shrink-0 flex-col gap-3 overflow-y-auto">
                  <AiBanner
                    phase={aiPhase}
                    filledCount={aiFilledCount}
                    isLongWait={isLongWait}
                  />
                  <SelectedFile file={form.values.file} />
                  <TextInput
                    placeholder={form.values.file?.name ?? 'Enter a file name'}
                    label="File Name"
                    {...form.getInputProps('fileName')}
                  />
                  <Select
                    withAsterisk
                    label={categoryLabel}
                    placeholder="Select category"
                    disabled={isCategoryLocked}
                    data={categories}
                    searchable
                    required
                    {...form.getInputProps('category')}
                  />
                  <Select
                    withAsterisk
                    label="Status"
                    placeholder="Select status"
                    data={statusOptions}
                    required
                    {...form.getInputProps('userStatus')}
                  />
                  {showExpirationDate && (
                    <div className="flex flex-col">
                      <DateInput
                        label={expirationLabel}
                        clearable
                        value={form.values.expirationDate}
                        onChange={(date) =>
                          form.setFieldValue('expirationDate', date)
                        }
                      />
                      <DateShortcutButton
                        onClick={(_event, date) =>
                          form.setFieldValue('expirationDate', date)
                        }
                      />
                    </div>
                  )}
                  <Divider variant="dashed" className="mt-auto" />
                  <div className="flex gap-3">
                    <DNButton
                      className="flex-1"
                      variant="outline"
                      onClick={handleClose}
                    >
                      Cancel
                    </DNButton>
                    <DNButton
                      className="flex-1"
                      variant="primary"
                      onClick={handleSaveAiValues}
                      loading={isSavingAi}
                      disabled={isSavingAi}
                    >
                      Save
                    </DNButton>
                  </div>
                </div>
              </div>
            )}

            {/* ── VoE upload: the picked file beside the form ── */}
            {hasVoEPreview && (
              <div
                className="flex gap-4"
                style={{ height: 'min(720px, 75vh)' }}
              >
                {previewPane}
                <div className="flex w-[400px] shrink-0 flex-col overflow-y-auto">
                  {voeFormFields}
                  {footer}
                </div>
              </div>
            )}

            {/* ── Compact layout: loading state or non-previewable post-upload ── */}
            {!hasPreview &&
              !hasVoEPreview &&
              (form.values.file || isPostUpload) &&
              (voe ? (
                voeFormFields
              ) : (
                <>
                  {(isLoadingPhase || isPostUpload) && (
                    <div className="mb-4">
                      {isLoadingPhase ? (
                        <AiAnalyzingBanner isLongWait={isLongWait} />
                      ) : (
                        <AiBanner
                          phase={aiPhase}
                          filledCount={aiFilledCount}
                          isLongWait={isLongWait}
                        />
                      )}
                    </div>
                  )}

                  <SelectedFile
                    file={form.values.file}
                    onClose={
                      isLoadingPhase || isPostUpload
                        ? undefined
                        : cleanSelectedFile
                    }
                  />

                  <div className="mt-3 grid grid-cols-2 gap-4">
                    <TextInput
                      className="col-span-2"
                      placeholder={
                        form.values.file?.name ?? 'Enter a file name'
                      }
                      label="File Name"
                      disabled={isLoadingPhase}
                      {...form.getInputProps('fileName')}
                    />

                    {isLoadingPhase ? (
                      <div className="flex flex-col gap-1">
                        <span className="text-sm font-medium text-doubleNickel-gray-700">
                          Category <span className="text-red-500">*</span>
                        </span>
                        <Skeleton height={36} radius="md" />
                      </div>
                    ) : (
                      <Select
                        withAsterisk
                        label={isPostUpload ? categoryLabel : 'Category'}
                        placeholder="Select category"
                        disabled={isCategoryLocked}
                        data={categories}
                        searchable
                        required
                        {...form.getInputProps('category')}
                      />
                    )}

                    <Select
                      withAsterisk
                      label="Status"
                      placeholder="Select status"
                      data={statusOptions}
                      required
                      {...form.getInputProps('userStatus')}
                    />

                    {showExpirationDate && (
                      <div className="flex flex-col">
                        {isLoadingPhase ? (
                          <>
                            <span className="mb-1 text-sm font-medium text-doubleNickel-gray-700">
                              Expiration Date
                            </span>
                            <Skeleton height={36} radius="md" />
                          </>
                        ) : (
                          <>
                            <DateInput
                              label={
                                isPostUpload
                                  ? expirationLabel
                                  : 'Expiration Date'
                              }
                              clearable
                              value={form.values.expirationDate}
                              onChange={(date) =>
                                form.setFieldValue('expirationDate', date)
                              }
                            />
                            <DateShortcutButton
                              onClick={(_event, date) =>
                                form.setFieldValue('expirationDate', date)
                              }
                            />
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ))}

            {!hasPreview && !hasVoEPreview && footer}
          </form>
        </div>
      </Modal>

      <BulkUploadDocumentReviewModal
        isOpen={showBulkReview && selectedFiles.length > 1}
        onClose={() => {
          setShowBulkReview(false)
          setSelectedFiles([])
        }}
        files={selectedFiles}
        onSubmit={handleBulkUploadSubmit}
        isVoEUpload={!!voe}
      />
    </>
  )
}

export default UploadDocumentModal
